import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  query,
  where,
  orderBy,
  Timestamp,
  getDocs,
} from "firebase/firestore";
import { db } from "../firebase";

/* ═══════════════════════════════════════════
   VENDORS
   Collection: vendors
   ═══════════════════════════════════════════ */

/** Real-time listener for all vendors in a community */
export const getVendorsInRealTime = (communityCode, cb) => {
  return onSnapshot(
    query(
      collection(db, "vendors"),
      where("communityCode", "==", communityCode)
    ),
    (snapshot) => {
      const vendors = [];
      snapshot.forEach((doc) => vendors.push({ id: doc.id, ...doc.data() }));
      vendors.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
      cb(vendors);
    },
    (error) => {
      console.error("Error fetching vendors:", error);
      cb([]);
    }
  );
};

/** One-shot fetch of active vendors for a community (for dropdowns) */
export const getActiveVendors = async (communityCode) => {
  const snap = await getDocs(
    query(collection(db, "vendors"), where("communityCode", "==", communityCode), where("status", "==", "active"))
  );
  const vendors = [];
  snap.forEach((doc) => vendors.push({ id: doc.id, ...doc.data() }));
  vendors.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  return vendors;
};

/** One-shot fetch of payout-eligible contracts for a vendor (confirmed or beyond) */
export const getActiveContracts = async (vendorId) => {
  const snap = await getDocs(collection(db, "vendors", vendorId, "contracts"));
  const contracts = [];
  snap.forEach((doc) => contracts.push({ id: doc.id, ...doc.data() }));
  const eligible = ["confirmed", "in_progress", "payment_done"];
  return contracts.filter((c) => eligible.includes(c.status));
};

/** Create a new vendor */
export const createVendor = async (vendorData) => {
  const payload = {
    ...vendorData,
    status: "active",
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now(),
  };
  const docRef = await addDoc(collection(db, "vendors"), payload);
  return docRef.id;
};

/** Update an existing vendor */
export const updateVendor = async (vendorId, vendorData) => {
  const ref = doc(db, "vendors", vendorId);
  return updateDoc(ref, { ...vendorData, updatedAt: Timestamp.now() });
};

/** Soft-delete: mark vendor inactive */
export const deactivateVendor = async (vendorId) => {
  const ref = doc(db, "vendors", vendorId);
  return updateDoc(ref, { status: "inactive", updatedAt: Timestamp.now() });
};

/** Hard-delete vendor (and all its contracts) */
export const deleteVendor = async (vendorId) => {
  // Delete all contracts first
  const contractsSnap = await getDocs(collection(db, "vendors", vendorId, "contracts"));
  const deletes = [];
  contractsSnap.forEach((d) => deletes.push(deleteDoc(d.ref)));
  await Promise.all(deletes);
  return deleteDoc(doc(db, "vendors", vendorId));
};

/* ═══════════════════════════════════════════
   VENDOR CONTRACTS
   Sub-collection: vendors/{vendorId}/contracts
   ═══════════════════════════════════════════ */

/** Real-time listener for all contracts of a vendor */
export const getContractsInRealTime = (vendorId, cb) => {
  return onSnapshot(
    query(
      collection(db, "vendors", vendorId, "contracts")
    ),
    (snapshot) => {
      const contracts = [];
      snapshot.forEach((doc) => contracts.push({ id: doc.id, ...doc.data() }));
      contracts.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
      cb(contracts);
    },
    (error) => {
      console.error("Error fetching contracts:", error);
      cb([]);
    }
  );
};

/** Create a new contract for a vendor */
export const createContract = async (vendorId, contractData) => {
  const payload = {
    ...contractData,
    vendorId,
    status: contractData.status || "active",
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now(),
  };
  const docRef = await addDoc(collection(db, "vendors", vendorId, "contracts"), payload);
  return docRef.id;
};

/** Update an existing contract */
export const updateContract = async (vendorId, contractId, contractData) => {
  const ref = doc(db, "vendors", vendorId, "contracts", contractId);
  return updateDoc(ref, { ...contractData, updatedAt: Timestamp.now() });
};

/** Delete a contract */
export const deleteContract = async (vendorId, contractId) => {
  return deleteDoc(doc(db, "vendors", vendorId, "contracts", contractId));
};
